/* =========================================================================
 * RUNTIME CONFIG — settings that change without a code change.
 *
 * Brian, 2026-09-18: the Studio's GPT points at his personal GPT for the
 * review and at the company GPT afterwards. Hardcoding it would make that
 * switch a code change, so it lives in /sr-runtime-config.json, fetched by the
 * browser on every page load and never compiled into the bundle.
 *
 * TWO LAYERS, most specific first:
 *   1. a per-device override an exec sets in the app (localStorage). Instant,
 *      this browser only. Meant for the demo machine.
 *   2. /sr-runtime-config.json on this site. Shared by everyone; changing it
 *      is a one-line edit and a publish of the static site, no code change.
 * A truly shared, no-publish setting needs a backend store, and
 * sr-portal-backend is not modified from this repo (hard rule 4). That gap is
 * stated rather than papered over.
 *
 * The file can be missing (the SPA fallback then answers with index.html and
 * a 200), so anything that is not JSON is treated as "no file", never trusted.
 * ========================================================================= */
import { useEffect, useState } from 'react'

export const RUNTIME_CONFIG_PATH = '/sr-runtime-config.json'
export const GPT_OVERRIDE_KEY = 'sr.artworkGpt.url.override'
/* ⚠️ TWO GPTs, NOT ONE. SR Artwork Engine Director answers the assembled ring
 * prompt; the Bezel Drop sheet is a different job with a different GPT (SR
 * Bezel Drop Director, g-6a90a461e8848191abc489e67e0d1491, image generation
 * enabled 2026-09-18). Pointing the sheet at the ring GPT is how you get a
 * written build spec instead of a sheet, which is the failure already recorded
 * against artworkGpt in sr-runtime-config.json. Each target carries its own
 * device override so fixing one on the demo machine cannot break the other. */
export const BEZEL_GPT_OVERRIDE_KEY = 'sr.bezelDropGpt.url.override'

/** Only a ChatGPT GPT link is accepted: anything else would send the prompt somewhere unexpected. */
export function isGptUrl(url) {
  return /^https:\/\/(chatgpt\.com|chat\.openai\.com)\/g\/g-[A-Za-z0-9]+(-[\w-]*)?\/?$/.test(String(url || '').trim())
}

const DEFAULTS = Object.freeze({
  artworkGpt: { url: '', label: 'Artwork GPT', owner: '' },
  bezelDropGpt: { url: '', label: 'Bezel Drop GPT', owner: '' },
  referenceSet: [],
  referenceSetNote: '',
  attachJobCrest: true,
})

/** One GPT target, resolved the same way whichever job it serves. */
function resolveGpt(raw, override, fallbackLabel) {
  const g = raw && typeof raw === 'object' ? raw : {}
  const fileUrl = isGptUrl(g.url) ? String(g.url).trim() : ''
  const overrideUrl = isGptUrl(override) ? String(override).trim() : ''
  return {
    url: overrideUrl || fileUrl,
    label: String(g.label || fallbackLabel),
    owner: String(g.owner || ''),
    source: overrideUrl ? 'this device' : fileUrl ? 'site config' : 'not configured',
    siteUrl: fileUrl,
    // Optional line put ABOVE the assembled prompt, for a GPT that has to be
    // told to draw. Empty by default: the assembled prompt is sent unchanged.
    promptPrefix: String(g.promptPrefix || '').slice(0, 500),
  }
}

/**
 * Pure: merge a fetched file and the device overrides into one answer, with
 * where each part came from. `bezelOverride` is a third argument on purpose —
 * every existing caller keeps working unchanged.
 */
export function resolveRuntimeConfig(file, override, bezelOverride) {
  const f = file && typeof file === 'object' ? file : {}
  const refs = Array.isArray(f.referenceSet) ? f.referenceSet : []
  return {
    artworkGpt: resolveGpt(f.artworkGpt, override, DEFAULTS.artworkGpt.label),
    bezelDropGpt: resolveGpt(f.bezelDropGpt, bezelOverride, DEFAULTS.bezelDropGpt.label),
    referenceSet: refs
      .map((r) => (typeof r === 'string' ? { url: r } : r))
      .filter((r) => r && /^(https:\/\/|\/)[^\s]+$/.test(String(r.url || '')))
      .map((r) => ({ url: String(r.url), name: String(r.name || String(r.url).split('/').pop() || 'reference') })),
    referenceSetNote: String(f.referenceSetNote || ''),
    attachJobCrest: f.attachJobCrest !== false,
  }
}

export function readGptOverride() {
  try { return localStorage.getItem(GPT_OVERRIDE_KEY) || '' } catch { return '' }
}

/** Set ('' clears). Returns false when the value is not a GPT link or storage is unavailable. */
export function writeGptOverride(url) {
  const v = String(url || '').trim()
  if (v && !isGptUrl(v)) return false
  try {
    if (v) localStorage.setItem(GPT_OVERRIDE_KEY, v)
    else localStorage.removeItem(GPT_OVERRIDE_KEY)
    return true
  } catch {
    return false
  }
}

export function readBezelGptOverride() {
  try { return localStorage.getItem(BEZEL_GPT_OVERRIDE_KEY) || '' } catch { return '' }
}

/** Set ('' clears) the Bezel Drop GPT for this device only. */
export function writeBezelGptOverride(url) {
  const v = String(url || '').trim()
  if (v && !isGptUrl(v)) return false
  try {
    if (v) localStorage.setItem(BEZEL_GPT_OVERRIDE_KEY, v)
    else localStorage.removeItem(BEZEL_GPT_OVERRIDE_KEY)
    return true
  } catch {
    return false
  }
}

let cached = null
/** Fetch once per page load. Never throws: a missing or broken file resolves to defaults. */
export function loadRuntimeFile() {
  if (!cached) {
    cached = fetch(RUNTIME_CONFIG_PATH, { cache: 'no-store', credentials: 'same-origin' })
      .then(async (r) => {
        const ct = r.headers.get('content-type') || ''
        if (!r.ok || !ct.includes('json')) return null
        return r.json().catch(() => null)
      })
      .catch(() => null)
  }
  return cached
}

/** The resolved config, re-resolved when the device override changes. */
export function useRuntimeConfig() {
  const [file, setFile] = useState(null)
  const [override, setOverride] = useState(readGptOverride)
  const [bezelOverride, setBezelOverride] = useState(readBezelGptOverride)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => {
    let alive = true
    loadRuntimeFile().then((f) => { if (alive) { setFile(f); setLoaded(true) } })
    return () => { alive = false }
  }, [])
  const config = resolveRuntimeConfig(file, override, bezelOverride)
  /* `which` names the TARGET, not the value. Without it the Bezel Drop panel's
   * "change the GPT for this device" would silently repoint the ring renders
   * too, and the person fixing one thing mid-demo would break the other. */
  const setDeviceGpt = (url, which = 'artworkGpt') => {
    const bezel = which === 'bezelDropGpt'
    const ok = bezel ? writeBezelGptOverride(url) : writeGptOverride(url)
    if (ok) (bezel ? setBezelOverride : setOverride)(String(url || '').trim())
    return ok
  }
  return { config, loaded, fileFound: !!file, setDeviceGpt }
}
